import { Button, Modal, Input, Select, DatePicker } from '@ds';
import React, { useContext, useMemo, useState } from 'react';
import styles from './VerifyPassportModal.module.scss';
import { ButtonSize, ButtonStyle, ButtonVariant } from '@ds/elements/Button/Button.types';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { IOption } from '@ds/elements/Select/Select.types';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { IPassportInfoMetaDataRequest } from 'src/shared/types/ImmigrationDocument';
import { IPassportInfo } from 'src/shared/types/Documents';
import classNames from 'classnames';

interface VerifyPassportModalProps {
  onClose: () => void;
  isReadOnly: boolean;
  onFinishPassport?: (payload: IPassportInfoMetaDataRequest) => Promise<void>;
  issuedToOptions?: IOption[];
  isVisible: boolean;
  initialFormData: IPassportInfo; // New prop for form values
}

const TITLES = {
  fillOutInformation: 'FILL OUT INFORMATION',
  submitPassport: 'Submit Passport',
  instructionText: 'Please fill in all the fields to complete your passport',
  issuedTo: 'Issued To',
  countryLocation: 'Country/Location',
  locationOfPassport: 'Location of Passport',
  typeOfPassport: 'Type of Passport',
  passportNumber: 'Passport Number',
  expirationDate: 'Expiration Date',
  issuedDate: 'Issued Date',
  issuedAtCountry: 'Issued at Country',
  issuingAuthority: 'Issuing Authority',
  issuedAtState: 'Issued at State/Province',
  issuedAtCity: 'Issued at City',
  hasEnoughBlanks: 'Has Enough Blank Pages',
  isTemporary: 'Is Temporary',
  isBiometric: 'Is Biometric',
};

const VerifyPassportModal: React.FC<VerifyPassportModalProps> = ({
  onClose,
  isReadOnly,
  onFinishPassport,
  issuedToOptions,
  isVisible = false,
  initialFormData,
}) => {
  const { beneId } = useContext(BeneDocAdditionalContext);
  const [formData, setFormData] = useState<IPassportInfoMetaDataRequest>({
    country: initialFormData.country || '',
    passportNumber: initialFormData.passportNumber || '',
    issuedTo: initialFormData.issuedTo || 0,
    issuedToBene: initialFormData.issuedTo === beneId || false,
    issuedDate: (initialFormData.issuedDate as unknown as string) || '',
    issuingAuthority: initialFormData.issuingAuthority || '',
    passportIssuedCountry: initialFormData.passportIssuedCountry || '',
    passportExpirationDate: (initialFormData.passportExpirationDate as unknown as string) || '',
    issuedState: initialFormData.issuedState || '',
    issuedCity: initialFormData.issuedCity || '',
    passportType: initialFormData.passportType || '',
    locationOfPassport: initialFormData.locationOfPassport || '',
    hasEnoughBlanks: initialFormData.hasEnoughBlanks ?? null,
    isTemporary: initialFormData.isTemporary ?? null,
    isBiometric: initialFormData.isBiometric ?? null,
    isInactive: initialFormData.isInactive || false,
  });
  const { data: passportLocations } = useGetReferenceData('LOCATION_OF_PASSPORT');
  const { data: passportTypes } = useGetReferenceData('TYPE_OF_PASSPORT');
  const { data: countries } = useGetCountries();

  const memoizedPassportLocations = useMemo(
    () =>
      passportLocations?.map(({ code, description }) => ({
        label: description,
        value: code,
      })) || [],
    [passportLocations]
  );

  const memoizedPassportTypes = useMemo(
    () =>
      passportTypes?.map(({ code, description }) => ({
        label: description,
        value: code,
      })) || [],
    [passportTypes]
  );

  const memoizedCountries = useMemo(
    () =>
      countries?.map(({ countryCode, countryName }) => ({
        label: countryName,
        value: countryCode,
      })) || [],
    [countries]
  );

  const issuedToName = issuedToOptions?.find((option) => option.value === formData.issuedTo)?.label;

  const handleChange = (key: keyof IPassportInfoMetaDataRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const YesNoButtonGroup: React.FC<{
    label: string;
    value: boolean | null | undefined;
    onChange: (value: boolean) => void;
  }> = ({ label, value, onChange }) => (
    <div className={styles['yes-no-group']}>
      <span className={styles['label']}>{label}</span>
      <div className={styles['buttons']}>
        <Button
          styleType={ButtonStyle.secondary}
          className={classNames(styles['button'], isReadOnly && value !== true && styles['not-selected'])}
          onClick={() => onChange(true)}
          variant={value === true ? ButtonVariant.contained : ButtonVariant.outlined}
          disabled={isReadOnly}
        >
          Yes
        </Button>
        <Button
          styleType={ButtonStyle.secondary}
          className={classNames(styles['button'], isReadOnly && value !== false && styles['not-selected'])}
          variant={value === false ? ButtonVariant.contained : ButtonVariant.outlined}
          onClick={() => onChange(false)}
          disabled={isReadOnly}
        >
          No
        </Button>
      </div>
    </div>
  );

  const submitPassport = async () => {
    const issuedToBene = formData.issuedTo === beneId;
    const payload = {
      ...formData,
      issuedToBene,
    };
    onFinishPassport && onFinishPassport(payload);
    onClose();
  };

  const allowSubmit =
    !!formData.issuedTo &&
    !!formData.country &&
    !!formData.locationOfPassport &&
    !!formData.passportType &&
    !!formData.passportNumber &&
    !!formData.issuedDate &&
    !!formData.passportExpirationDate;

  return (
    <Modal
      data-leave-modal-content
      visible={isVisible}
      className={styles['verify-passport-modal']}
      modalClassName={styles['verify-passport-modal-content-container']}
      onModalClosed={onClose}
      disableClickOutside
      title=""
      fullheight
      fullwidth
    >
      <div className={styles['verify-header']} data-leave-modal-content>
        <p>{TITLES.fillOutInformation}</p>
        {!isReadOnly && onFinishPassport && (
          <Button
            className={styles['submit-button']}
            onClick={() => {
              submitPassport();
            }}
            size={ButtonSize.large}
            disabled={!allowSubmit}
          >
            {TITLES.submitPassport}
          </Button>
        )}
      </div>
      <div className={styles['body']} data-leave-modal-content>
        <p className={styles['instruction-text']}>{TITLES.instructionText}</p>
        <div className={styles['row']}>
          <Select
            className={styles['input']}
            placeholder=""
            label={TITLES.issuedTo}
            required
            options={issuedToOptions || []}
            selectedOption={{ label: issuedToName || '', value: formData.issuedTo }}
            onChange={(value) => handleChange('issuedTo', value?.value)}
            disabled={isReadOnly}
          />
          <Select
            className={styles['input']}
            placeholder=""
            label={TITLES.countryLocation}
            required
            options={memoizedCountries}
            searchable
            selectedOption={{ label: formData.country, value: formData.country }}
            onChange={(value) => handleChange('country', value?.value)}
            disabled={isReadOnly}
          />
          <Select
            className={styles['input']}
            placeholder=""
            label={TITLES.locationOfPassport}
            required
            options={memoizedPassportLocations}
            selectedOption={{ label: formData.locationOfPassport, value: formData.locationOfPassport }}
            onChange={(value) => handleChange('locationOfPassport', value?.value)}
            disabled={isReadOnly}
          />
        </div>

        <div className={styles['row']}>
          <Select
            className={styles['input']}
            label={TITLES.typeOfPassport}
            required
            placeholder=""
            options={memoizedPassportTypes}
            selectedOption={{ label: formData.passportType, value: formData.passportType }}
            onChange={(value) => handleChange('passportType', value?.value)}
            disabled={isReadOnly}
          />
          <Input
            className={styles['input']}
            label={TITLES.passportNumber}
            required
            value={formData.passportNumber}
            onChange={(event) => handleChange('passportNumber', event.target.value)}
            disabled={isReadOnly}
          />
        </div>

        <div className={styles['row']}>
          <DatePicker
            className={styles['input']}
            panelPosition="below"
            label={TITLES.expirationDate}
            value={formData.passportExpirationDate as unknown as string}
            required
            onChange={(date) => handleChange('passportExpirationDate', date?.toISOString() || '')}
            disabled={isReadOnly}
          />
          <DatePicker
            className={styles['input']}
            panelPosition="below"
            label={TITLES.issuedDate}
            required
            value={formData.issuedDate as unknown as string}
            onChange={(date) => handleChange('issuedDate', date?.toISOString() || '')}
            disabled={isReadOnly}
          />
          <Select
            className={styles['input']}
            label={TITLES.issuedAtCountry}
            options={memoizedCountries}
            searchable
            placeholder=""
            selectedOption={{ label: formData.passportIssuedCountry, value: formData.passportIssuedCountry }}
            onChange={(value) => handleChange('passportIssuedCountry', value?.value)}
            disabled={isReadOnly}
          />
        </div>
        <div className={styles['row']}>
          <Input
            className={styles['input']}
            label={TITLES.issuingAuthority}
            value={formData.issuingAuthority}
            onChange={(e) => handleChange('issuingAuthority', e.target.value)}
            disabled={isReadOnly}
          />
          <Input
            className={styles['input']}
            label={TITLES.issuedAtState}
            value={formData.issuedState}
            onChange={(e) => handleChange('issuedState', e.target.value)}
            disabled={isReadOnly}
          />
          <Input
            className={styles['input']}
            label={TITLES.issuedAtCity}
            value={formData.issuedCity}
            onChange={(e) => handleChange('issuedCity', e.target.value)}
            disabled={isReadOnly}
          />
        </div>
        <div className={styles['yes-no-row']}>
          <YesNoButtonGroup
            label={TITLES.hasEnoughBlanks}
            value={formData.hasEnoughBlanks}
            onChange={(value) => handleChange('hasEnoughBlanks', value)}
          />

          <YesNoButtonGroup
            label={TITLES.isTemporary}
            value={formData.isTemporary}
            onChange={(value) => handleChange('isTemporary', value)}
          />

          <YesNoButtonGroup
            label={TITLES.isBiometric}
            value={formData.isBiometric}
            onChange={(value) => handleChange('isBiometric', value)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default VerifyPassportModal;
